import { useCallback, useEffect, useState } from "react";

import { clean } from "rut.js";

import { useToast } from "~/hooks/use-toast";
import { useEnsureProfile } from "~/hooks/useEnsureProfile";
import {
  deletePaymentProof,
  getClientPaymentProofs,
  updatePaymentProof,
  uploadPaymentScreenshot,
} from "~/services/paymentProofService";
import { useProfileStore } from "~/store/profileStore";
import {
  CreatePaymentProofData,
  PaymentProof,
  UpdatePaymentProofData,
} from "~/types/payment";
import { callEdgeFunction } from "~/utils/edgeFunctionHelper";

export const usePaymentProofs = (clientId: string) => {
  const [paymentProofs, setPaymentProofs] = useState<PaymentProof[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { id: userId } = useProfileStore();
  const { isProfileLoaded, refreshProfile } = useEnsureProfile();

  // Add diagnostic logging for user ID
  useEffect(() => {
    console.log("Current user ID in usePaymentProofs:", userId);

    // If no userId is available, try to refresh the profile
    if (!userId) {
      console.log(
        "No user ID available in usePaymentProofs, trying to refresh profile"
      );
      refreshProfile();
    }
  }, [userId, refreshProfile]);

  // Fetch payment proofs for the client
  const fetchPaymentProofs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getClientPaymentProofs(clientId);
      setPaymentProofs(data);
    } catch (err) {
      setError("Failed to load payment proofs");
      toast({
        title: "Error",
        description: "No se pudieron cargar los comprobantes de pago",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [clientId, toast]);

  // Initial fetch on mount
  useEffect(() => {
    if (clientId) {
      fetchPaymentProofs();
    }
  }, [clientId, fetchPaymentProofs]);

  // Helper to validate user ID
  const validateUserId = useCallback((): string => {
    if (!userId) {
      // Try to refresh profile one more time before failing
      refreshProfile();

      // Check again after refresh attempt
      const currentUserId = useProfileStore.getState().id;
      if (!currentUserId) {
        throw new Error(
          "User ID is not available. Please reload the page or log in again."
        );
      }
      return currentUserId;
    }
    return userId;
  }, [userId, refreshProfile]);

  // Create a new payment proof
  const addPaymentProof = async (
    proofData: Omit<CreatePaymentProofData, "clientId">,
    file?: File
  ) => {
    try {
      // Ensure profile is loaded before proceeding
      if (!isProfileLoaded) {
        await refreshProfile();
      }

      setError(null);
      setIsSubmitting(true);

      // Ensure we have a user ID
      const currentUserId = validateUserId();

      // First upload the file if provided
      let screenshotUrl: string | undefined = proofData.screenshotUrl;

      if (file) {
        setIsUploading(true);
        try {
          // Handle file upload via the service
          const uploadUrl = await uploadPaymentScreenshot(file, clientId);
          if (uploadUrl) {
            screenshotUrl = uploadUrl;
            console.log("Screenshot uploaded successfully:", uploadUrl);
          } else {
            throw new Error("Failed to upload screenshot: No URL returned");
          }
        } catch (uploadError) {
          console.error("Screenshot upload error:", uploadError);
          toast({
            title: "Error en la carga de imagen",
            description:
              "No se pudo cargar la imagen, pero se continuará con el registro del pago",
            variant: "destructive",
          });
          // Continue without the screenshot
          screenshotUrl = undefined;
        } finally {
          setIsUploading(false);
        }
      }

      // Clean RUT to remove any formatting
      const cleanRut = clean(proofData.rut);

      // Call the Edge Function to create the payment proof
      const response = await callEdgeFunction("create-payment-proof", {
        clientId,
        rut: cleanRut,
        amount: proofData.amount,
        salesRequestId: proofData.salesRequestId || null,
        comments: proofData.comments || null,
        screenshotUrl: screenshotUrl || null,
        createdBy: currentUserId,
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to create payment proof");
      }

      const { paymentProof, linkedMovement } = response;

      if (paymentProof) {
        const newProof: PaymentProof = {
          id: paymentProof.id,
          createdAt: paymentProof.created_at,
          updatedAt: paymentProof.updated_at || undefined,
          createdBy: paymentProof.created_by,
          clientId: paymentProof.client_id,
          rut: paymentProof.rut,
          amount: paymentProof.amount,
          screenshotUrl: paymentProof.screenshot_url || undefined,
          salesRequestId: paymentProof.sales_request_id || undefined,
          comments: paymentProof.comments || undefined,
        };

        setPaymentProofs((prev) => [newProof, ...prev]);
        toast({
          title: "Comprobante creado",
          description:
            "El comprobante de pago ha sido registrado correctamente",
        });

        // Show additional notification if a movement was linked
        if (linkedMovement) {
          toast({
            title: "Movimiento bancario vinculado",
            description:
              "Se ha asociado automáticamente un movimiento bancario con esta nota de venta",
            variant: "default",
          });
        }

        return newProof;
      } else {
        throw new Error("Failed to create payment proof: No data returned");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error desconocido";
      console.error("Error creating payment proof:", err);
      setError(`Failed to create payment proof: ${errorMessage}`);
      toast({
        title: "Error",
        description: "No se pudo crear el comprobante de pago",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update an existing payment proof
  const editPaymentProof = async (
    id: string,
    updates: UpdatePaymentProofData,
    file?: File
  ) => {
    try {
      setError(null);
      setIsSubmitting(true);

      // First upload the file if provided
      if (file) {
        setIsUploading(true);
        try {
          const uploadUrl = await uploadPaymentScreenshot(file, clientId);
          if (uploadUrl) {
            updates.screenshotUrl = uploadUrl;
          } else {
            throw new Error("Failed to upload screenshot");
          }
        } catch (uploadError) {
          console.error("Screenshot upload error during edit:", uploadError);
          toast({
            title: "Error en la carga de imagen",
            description:
              "No se pudo actualizar la imagen, pero se continuará con la actualización del pago",
            variant: "destructive",
          });
          // Continue without updating the screenshot
        } finally {
          setIsUploading(false);
        }
      }

      // Then update the payment proof
      const updatedProof = await updatePaymentProof(id, updates);

      if (updatedProof) {
        setPaymentProofs((prev) =>
          prev.map((proof) => (proof.id === id ? updatedProof : proof))
        );
        toast({
          title: "Comprobante actualizado",
          description:
            "El comprobante de pago ha sido actualizado correctamente",
        });
        return updatedProof;
      } else {
        throw new Error("Failed to update payment proof");
      }
    } catch (err) {
      setError("Failed to update payment proof");
      toast({
        title: "Error",
        description: "No se pudo actualizar el comprobante de pago",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete a payment proof
  const removePaymentProof = async (id: string) => {
    try {
      setError(null);
      setIsSubmitting(true);
      const success = await deletePaymentProof(id);

      if (success) {
        setPaymentProofs((prev) => prev.filter((proof) => proof.id !== id));
        toast({
          title: "Comprobante eliminado",
          description: "El comprobante de pago ha sido eliminado correctamente",
        });
        return true;
      } else {
        throw new Error("Failed to delete payment proof");
      }
    } catch (err) {
      setError("Failed to delete payment proof");
      toast({
        title: "Error",
        description: "No se pudo eliminar el comprobante de pago",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    paymentProofs,
    loading,
    error,
    isUploading,
    isSubmitting,
    fetchPaymentProofs,
    addPaymentProof,
    editPaymentProof,
    removePaymentProof,
    isProfileLoaded,
  };
};
